import { useMemo, useState } from 'react'
import { formatMoney } from '../../lib/money'
import { useStore } from '../../store'
import { Dialog, Money, Sheet } from '../../ui/parts'
import { shortMonthLabel, monthLabel } from '../engine'
import { buy, checkAffordability, type ItemView } from '../repo'
import type { PurchasePlan } from '../types'

/**
 * Opens on Check. Shows what the purchase would do to this month and the months after it,
 * and spends nothing until Buy is pressed. Buy is the only control in the app that moves
 * money, and the repository re-validates before writing.
 */
export function CheckSheet({ item, onClose }: { item: ItemView; onClose: () => void }) {
  const { snapshot, mutate, refresh, showToast } = useStore()
  const [plan, setPlan] = useState<PurchasePlan>('full')
  const [confirmForce, setConfirmForce] = useState(false)
  const [busy, setBusy] = useState(false)

  const result = useMemo(
    () => (snapshot ? checkAffordability(snapshot, item.id, plan) : null),
    [snapshot, item.id, plan]
  )

  if (!snapshot) return null

  async function purchase(force: boolean) {
    if (!snapshot) return
    setBusy(true)
    setConfirmForce(false)

    // Evaluate against the snapshot on screen to learn the outcome, then commit the
    // resulting snapshot. The rules run again inside `buy`, so a stale sheet is caught.
    const outcome = buy(snapshot, item.id, plan, item.effectivePriceFils, force)

    switch (outcome.result.kind) {
      case 'success': {
        const { itemTitle, plan: bought } = outcome.result
        const saved = await mutate(() => outcome.snapshot)
        setBusy(false)
        if (saved) {
          onClose()
          showToast(
            `Bought "${itemTitle}" — ${bought === 'split4' ? 'split over 4 months' : 'paid in full'}`
          )
        }
        break
      }
      case 'not-affordable':
        setBusy(false)
        showToast(
          `Not affordable — short by ${formatMoney(outcome.result.shortfallFils)} AED in ${monthLabel(outcome.result.month)}`
        )
        break
      case 'price-changed':
        setBusy(false)
        showToast(
          `The price changed to ${formatMoney(outcome.result.actualPriceFils)} AED — check again before buying`
        )
        await refresh()
        break
      case 'already-purchased':
        setBusy(false)
        onClose()
        showToast('That item was already bought')
        break
      case 'invalid':
        setBusy(false)
        showToast(outcome.result.reason)
        break
    }
  }

  return (
    <Sheet onClose={onClose}>
      <div className="label">Check budget</div>

      <div className="row" style={{ marginTop: 10, marginBottom: 16 }}>
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt=""
            style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover' }}
          />
        ) : null}
        <div className="grow">
          <div style={{ fontWeight: 650 }}>{item.title}</div>
          {item.brand ? <div className="muted" style={{ fontSize: 13 }}>{item.brand}</div> : null}
        </div>
        <div style={{ fontWeight: 700 }}>
          <Money fils={item.effectivePriceFils} currency />
        </div>
      </div>

      <div className="segmented">
        <button type="button" className={plan === 'full' ? 'on' : ''} onClick={() => setPlan('full')}>
          Pay in full
        </button>
        <button
          type="button"
          className={plan === 'split4' ? 'on' : ''}
          onClick={() => setPlan('split4')}
        >
          Split over 4 months
        </button>
      </div>

      {result ? (
        <>
          <table className="check-table" style={{ marginTop: 18 }}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Available</th>
                <th>This buy</th>
                <th>Left after</th>
              </tr>
            </thead>
            <tbody>
              {result.months.map((row) => (
                <tr key={row.month} className={row.ok ? '' : 'fail'}>
                  <td>{shortMonthLabel(row.month)}</td>
                  <td>{formatMoney(row.availableBeforeFils)}</td>
                  <td className="muted">
                    {row.chargeFils === 0 ? '—' : `-${formatMoney(row.chargeFils)}`}
                  </td>
                  <td>
                    <Money fils={row.closingAfterFils} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={`verdict ${result.ok ? 'ok' : 'bad'}`} style={{ marginTop: 16 }}>
            {result.ok ? (
              'Affordable'
            ) : (
              <>
                Not affordable — short by {formatMoney(result.shortfallFils)} AED in{' '}
                {monthLabel(result.shortfallMonth ?? result.months[0].month)}
                {plan === 'full' ? (
                  <div style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      className="btn ghost small"
                      style={{ paddingLeft: 0 }}
                      onClick={() => setPlan('split4')}
                    >
                      Try splitting over 4 months
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <button
            type="button"
            className="btn block"
            style={{ marginTop: 18 }}
            disabled={!result.ok || busy}
            onClick={() => void purchase(false)}
          >
            {busy
              ? 'Buying…'
              : plan === 'split4'
                ? `Buy — ${formatMoney(result.installments[0].amountFils)} AED now`
                : 'Buy'}
          </button>

          {!result.ok ? (
            <button
              type="button"
              className="btn danger block"
              style={{ marginTop: 6 }}
              onClick={() => setConfirmForce(true)}
            >
              Buy anyway
            </button>
          ) : null}
        </>
      ) : (
        <div className="muted" style={{ marginTop: 18 }}>
          Could not work out the budget for this item.
        </div>
      )}

      <button type="button" className="btn ghost block" style={{ marginTop: 4 }} onClick={onClose}>
        Cancel
      </button>

      {confirmForce && result ? (
        <Dialog
          title="Go over budget?"
          body={`This leaves ${monthLabel(result.shortfallMonth ?? result.months[0].month)} at -${formatMoney(result.shortfallFils)} AED. The deficit carries forward into the following month.`}
          confirmLabel="Buy anyway"
          danger
          onCancel={() => setConfirmForce(false)}
          onConfirm={() => void purchase(true)}
        />
      ) : null}
    </Sheet>
  )
}
